import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, Copy, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { userService } from '../../_services';
import copy from 'copy-to-clipboard';

function AdvanceSetting({
    titleText,
    whName,
    whId,
    whPrefix,
    whProfilePagin,
    whSearch,
    isGlic,
    closeSettingPopup,
    openEditProfilePopup
}) {
    const [loading, setLoading] = useState(false);
    const [btnLoader, setBtnLoader] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [settings, setSettings] = useState({});
    const [copyIdText, setCopyIdText] = useState('Copy List Id');
    const [copyNameText, setCopyNameText] = useState('Copy List Name');

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const fetchAdvanceSetting = useCallback(async (roleName, roleType, id) => {
        setLoading(true);
        setError('');
        
        try {
            const apidata = await userService.GetAdvanceSetting(roleName, roleType, id);
            
            if (apidata.length > 0) {
                setSettings(apidata[0].settings[0] || {});
            } else {
                setError('Oops!, Something went wrong. Try Again.');
            }
        } catch (error) {

            setError('Oops!, Something went wrong. Try Again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (userdata && whId) {
            fetchAdvanceSetting(userdata.username, userdata.userrole, whId);
        }
    }, [userdata, whId, fetchAdvanceSetting]);

    const handleSettingChange = useCallback((field, value) => {
        setSettings(prev => ({ ...prev, [field]: value === 'yes' }));
    }, []);

    const copyToClipboard = useCallback((text, type) => {
        copy(text);
        if (type === 'id') {
            setCopyIdText('Copied');
            setTimeout(() => setCopyIdText('Copy List Id'), 2000);
        } else {
            setCopyNameText('Copied');
            setTimeout(() => setCopyNameText('Copy List Name'), 2000);
        }
    }, []);

    const handleSave = useCallback(async (e) => {
        e.preventDefault();
        setBtnLoader(true);
        setError('');
        setSuccessMessage('');

        try {
            const apidata = await userService.UpdatedAdvanceSetting(
                whId,
                settings.listId,
                settings.listName,
                settings.isBroker,
                settings.isWhCC,
                settings.isPricingEndpoint,
                settings.isDataEndpoint,
                settings.isEnrollEndpoint,
                settings.isBrokerEndpoint,
                settings.isBrokerWelcomeEmail,
                settings.isAddBrokerHS,
                settings.isBrokerCredEmail,
                settings.isAddBrokerKlaviyo,
                settings.isPharmacyEndpoint,
                settings.isPassesEndpoint,
                settings.isFTPAccess
            );

            if (apidata?.length > 0) {
                if (apidata[0].status_code === "1") {
                    setSuccessMessage(apidata[0].status_message);
                    setTimeout(() => {
                        handleClose();
                    }, 2000);
                } else {
                    setError(apidata[0].status_message || 'Error!, Please try again.');
                }
            } else {
                setError('Error!, Please try again.');
            }
        } catch (error) {

            setError('Error!, Please try again.');
        } finally {
            setBtnLoader(false);
        }
    }, [settings, whId]);

    const handleClose = useCallback(() => {
        closeSettingPopup(whName, whId, whPrefix, whProfilePagin, whSearch);
        openEditProfilePopup(whName, whId, whPrefix, whProfilePagin, whSearch);
    }, [closeSettingPopup, openEditProfilePopup, whName, whId, whPrefix, whProfilePagin, whSearch]);

    return (
        <Dialog open={true} onOpenChange={handleClose}>
            <DialogContent className="max-w-5xl">
                <DialogHeader>
                    <DialogTitle>Advance Settings: {whName}</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center items-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <>
                    <form id="advance-setting-form" onSubmit={handleSave} className="space-y-6">
                        {isGlic === "true" && (
                            <div className="space-y-4 border-b pb-4 mb-4">
                                <h2 className="text-lg font-semibold mb-3">GlicRx</h2>
                                
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start mb-3">
                                    <div className="space-y-2">
                                        <Label>Enable Brokers</Label>
                                        <RadioGroup
                                            value={settings.isBroker ? 'yes' : 'no'}
                                            onValueChange={(value) => handleSettingChange('isBroker', value)}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="yes" id="broker-yes" />
                                                <Label htmlFor="broker-yes">Yes</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="no" id="broker-no" />
                                                <Label htmlFor="broker-no">No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>CC Wholesaler</Label>
                                        <RadioGroup
                                            value={settings.isWhCC ? 'yes' : 'no'}
                                            onValueChange={(value) => handleSettingChange('isWhCC', value)}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="yes" id="cc-yes" />
                                                <Label htmlFor="cc-yes">Yes</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="no" id="cc-no" />
                                                <Label htmlFor="cc-no">No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    {settings.listName && settings.listId && <div className="md:col-span-2 space-y-2">
                                        <Label>Sendgrid Details</Label>
                                        <div className="grid grid-cols-[auto_1fr_auto] gap-x-2 gap-y-2 items-start text-sm">
                                            <span className="font-medium">List ID:</span>
                                            <span className="min-w-0 break-all">{settings.listId}</span>
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => copyToClipboard(settings.listId, 'id')}
                                                            className="h-auto shrink-0"
                                                        >
                                                            {copyIdText === 'Copied' ? (
                                                                <Check className="h-4 w-4" />
                                                            ) : (
                                                                <Copy className="h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{copyIdText}</TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                            <span className="font-medium">List Name:</span>
                                            <span className="min-w-0 break-words">{settings.listName}</span>
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => copyToClipboard(settings.listName, 'name')}
                                                            className="h-auto shrink-0"
                                                        >
                                                            {copyNameText === 'Copied' ? (
                                                                <Check className="h-4 w-4" />
                                                            ) : (
                                                                <Copy className="h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{copyNameText}</TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    </div>}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start mb-0">
                                    <div className="space-y-2">
                                        <Label>Add Brokers to Hubspot</Label>
                                        <RadioGroup
                                            value={settings.isAddBrokerHS ? 'yes' : 'no'}
                                            onValueChange={(value) => handleSettingChange('isAddBrokerHS', value)}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="yes" id="hs-yes" />
                                                <Label htmlFor="hs-yes">Yes</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="no" id="hs-no" />
                                                <Label htmlFor="hs-no">No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Add Brokers to Klaviyo</Label>
                                        <RadioGroup
                                            value={settings.isAddBrokerKlaviyo ? 'yes' : 'no'}
                                            onValueChange={(value) => handleSettingChange('isAddBrokerKlaviyo', value)}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="yes" id="klaviyo-yes" />
                                                <Label htmlFor="klaviyo-yes">Yes</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="no" id="klaviyo-no" />
                                                <Label htmlFor="klaviyo-no">No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>FTP Access</Label>
                                        <RadioGroup
                                            value={settings.isFTPAccess ? 'yes' : 'no'}
                                            onValueChange={(value) => handleSettingChange('isFTPAccess', value)}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="yes" id="ftp-yes" />
                                                <Label htmlFor="ftp-yes">Yes</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="no" id="ftp-no" />
                                                <Label htmlFor="ftp-no">No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="space-y-4">
                            <h2 className="text-lg font-semibold mb-3">API</h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="space-y-2">
                                    <Label>Pricing Endpoint</Label>
                                    <RadioGroup
                                        value={settings.isPricingEndpoint ? 'yes' : 'no'}
                                        onValueChange={(value) => handleSettingChange('isPricingEndpoint', value)}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="yes" id="pricing-yes" />
                                            <Label htmlFor="pricing-yes">Yes</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="no" id="pricing-no" />
                                            <Label htmlFor="pricing-no">No</Label>
                                        </div>
                                    </RadioGroup>
                                </div>

                                <div className="space-y-2">
                                    <Label>Data Endpoint</Label>
                                    <RadioGroup
                                        value={settings.isDataEndpoint ? 'yes' : 'no'}
                                        onValueChange={(value) => handleSettingChange('isDataEndpoint', value)}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="yes" id="data-yes" />
                                            <Label htmlFor="data-yes">Yes</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="no" id="data-no" />
                                            <Label htmlFor="data-no">No</Label>
                                        </div>
                                    </RadioGroup>
                                </div>

                                <div className="space-y-2">
                                    <Label>Enrollment Endpoint</Label>
                                    <RadioGroup
                                        value={settings.isEnrollEndpoint ? 'yes' : 'no'}
                                        onValueChange={(value) => handleSettingChange('isEnrollEndpoint', value)}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="yes" id="enroll-yes" />
                                            <Label htmlFor="enroll-yes">Yes</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="no" id="enroll-no" />
                                            <Label htmlFor="enroll-no">No</Label>
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="space-y-2">
                                    <Label>Dashboard Endpoint</Label>
                                    <RadioGroup
                                        value={settings.isBrokerEndpoint ? 'yes' : 'no'}
                                        onValueChange={(value) => handleSettingChange('isBrokerEndpoint', value)}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="yes" id="dashboard-yes" />
                                            <Label htmlFor="dashboard-yes">Yes</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="no" id="dashboard-no" />
                                            <Label htmlFor="dashboard-no">No</Label>
                                        </div>
                                    </RadioGroup>
                                </div>

                                <div className="space-y-2">
                                    <Label>Pharmacy Endpoint</Label>
                                    <RadioGroup
                                        value={settings.isPharmacyEndpoint ? 'yes' : 'no'}
                                        onValueChange={(value) => handleSettingChange('isPharmacyEndpoint', value)}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="yes" id="pharmacy-yes" />
                                            <Label htmlFor="pharmacy-yes">Yes</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="no" id="pharmacy-no" />
                                            <Label htmlFor="pharmacy-no">No</Label>
                                        </div>
                                    </RadioGroup>
                                </div>

                                <div className="space-y-2">
                                    <Label>Passes Endpoint</Label>
                                    <RadioGroup
                                        value={settings.isPassesEndpoint ? 'yes' : 'no'}
                                        onValueChange={(value) => handleSettingChange('isPassesEndpoint', value)}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="yes" id="passes-yes" />
                                            <Label htmlFor="passes-yes">Yes</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="no" id="passes-no" />
                                            <Label htmlFor="passes-no">No</Label>
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>

                            {settings.isBrokerEndpoint && (
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 border-t pt-4">
                                    <div className="space-y-2">
                                        <Label>Broker Welcome Email</Label>
                                        <RadioGroup
                                            value={settings.isBrokerWelcomeEmail ? 'yes' : 'no'}
                                            onValueChange={(value) => handleSettingChange('isBrokerWelcomeEmail', value)}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="yes" id="welcome-yes" />
                                                <Label htmlFor="welcome-yes">Yes</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="no" id="welcome-no" />
                                                <Label htmlFor="welcome-no">No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Broker Credential Email</Label>
                                        <RadioGroup
                                            value={settings.isBrokerCredEmail ? 'yes' : 'no'}
                                            onValueChange={(value) => handleSettingChange('isBrokerCredEmail', value)}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="yes" id="cred-yes" />
                                                <Label htmlFor="cred-yes">Yes</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="no" id="cred-no" />
                                                <Label htmlFor="cred-no">No</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>
                                </div>
                            )}
                        </div>

                        {successMessage && (
                            <div className="text-sm text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400 p-3 rounded-md">
                                {successMessage}
                            </div>
                        )}

                        {error && (
                            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                                {error}
                            </div>
                        )}

                    </form>
                        <DialogFooter>
                            <Button type="submit" form="advance-setting-form" disabled={btnLoader}>
                                {btnLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Save & Submit
                            </Button>
                            <Button type="button" variant="secondary" onClick={handleClose} disabled={btnLoader}>
                                Cancel
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default AdvanceSetting;
